import { createFileRoute, isNotFound, Link } from '@tanstack/react-router';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { CampaignHero } from './-components/campaign-hero';
import { useLingui } from '@lingui/react/macro';
import { ChevronLeftIcon } from 'lucide-react';
import { Campaign } from './-components/campaign';
import { CampaignDetails } from './-components/campaign-details';
import { CampaignMetadata } from './-components/campaign-metadata';
import { usePortalCampaignMetadataQuery, usePortalCampaignQuery, usePortalMetadataQuery } from '@/lib/data-hooks';
import { Suspense, useEffect } from 'react';
import { buildAlternatesWithSlugs, Seo } from '../-components/seo';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { CampaignImageGallery } from './-components/campaign-image-gallery';
import { MobileDonateButton } from './-components/mobile-donate-button';
import { useEphemeralStore } from '@/lib/app-store';
import { ensureSeoData } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { preloadImageOnClient, heroImageSize } from '@worldcoo/ui/lib/utils';
import { sdgGoalUrl } from '@/lib/sdg-utils';
import { sdgMessages, categoryMessages } from '@/lib/portal-messages';
import { gtag } from '@worldcoo/ui/lib/analytics';
import { stripHtml } from '@worldcoo/shared/utils';
import { hasLocalePrefix } from '@/router';

export const Route = createFileRoute('/$portalId/$campaignId/')({
	loader: async ({ context, params }) => {
		context.queryClient.ensureQueryData(
			trpc.portal.getMetadata.queryOptions({
				locale: context.locale,
				portalId: context.portalId,
			}),
		);

		context.queryClient
			.ensureQueryData(trpc.portal.getAuthor.queryOptions({ locale: context.locale, portalId: context.portalId }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				const logoHeight = getLogoImageSize(context.portalId).height;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: logoHeight,
						width: Math.round(logoHeight * (data.logo.width / data.logo.height)),
						format: 'avif',
						quality: 60,
					}),
				);
			});

		await ensureSeoData(
			context,
			context.queryClient.ensureQueryData(
				trpc.portal.getCampaignMetadata.queryOptions({
					locale: context.locale,
					portalId: context.portalId,
					campaignId: params.campaignId,
				}),
			),
		);

		context.queryClient.ensureQueryData(
			trpc.portal.getCampaignsAggregates.queryOptions({
				portalId: context.portalId,
				campaignIds: [params.campaignId],
			}),
		);

		context.queryClient
			.ensureQueryData(
				trpc.portal.getCampaign.queryOptions({
					locale: context.locale,
					portalId: context.portalId,
					campaignId: params.campaignId,
				}),
			)
			.then((campaign) => {
				if ('notFound' in campaign) return;
				if (!campaign.media?.location) return;
				preloadImageOnClient(
					generateImageSrc(campaign.media.location, { ...heroImageSize, format: 'avif', quality: 60 }),
				);
			});
	},
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const { t } = useLingui();

	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	const setPagesLoad = useEphemeralStore((store) => store.setPagesLoaded);
	useEffect(() => {
		if (pagesLoaded.includes(`campaign-${campaignId}`)) return;
		const timeout = setTimeout(() => setPagesLoad([...pagesLoaded, `campaign-${campaignId}`]), 1500);
		return () => clearTimeout(timeout);
	}, [campaignId, pagesLoaded, setPagesLoad]);

	return (
		<>
			<Suspense>
				<SuspensedHead />
			</Suspense>
			{/* We're wrapping everything with the secondary color
			    because we need the sticky donate button at the same DOM level so it can work properly
			    and also we want to avoid white-on-grey color when showing to donate button at bottom in non-sticky mode,
			    hence the secondary color */}
			<div className="bg-secondary">
				<Header />
				<div className="bg-background pb-2 sm:pb-6">
					<nav className="mx-auto w-full max-w-[1366px]">
						<Link
							to="/$portalId"
							params={{ portalId }}
							search={(prev) => prev}
							className="mb-1 flex w-fit items-center gap-2 px-5 py-4 sm:mb-0 sm:p-6"
						>
							<ChevronLeftIcon />
							{t`Back to home`}
						</Link>
					</nav>

					<section className="mx-auto w-full max-w-[1366px]">
						<CampaignHero campaignId={campaignId} />
					</section>

					<CampaignImageGallery campaignId={campaignId} />
				</div>
				<div className="bg-secondary border-border border-t">
					<div className="mx-auto w-full max-w-[1366px] px-5 pt-6 sm:px-6 sm:pt-12 lg:pt-16">
						<CampaignMetadata campaignId={campaignId} />
					</div>
					<main className="mx-auto flex w-full max-w-[1366px] flex-col-reverse gap-6 px-5 pt-5 pb-6 sm:gap-12 sm:px-6 sm:pt-6 lg:flex-row lg:gap-24 lg:pb-16">
						<Campaign campaignId={campaignId} />
						{/**
						 * Add min height + stick to bottom instead to top so it can
						 * show the ngos in case the screen is too small
						 **/}
						<aside className="bottom-12 w-full flex-1 self-end lg:sticky lg:min-h-[calc(100dvh-150px)]">
							<CampaignDetails campaignId={campaignId} />
						</aside>
					</main>
				</div>
				<MobileDonateButton campaignId={campaignId} />
				<Footer />
			</div>
		</>
	);
}

function SuspensedHead() {
	const { campaignId } = Route.useParams();
	const { baseUrl, locale } = Route.useRouteContext();
	const { t } = useLingui();
	const metadata = usePortalCampaignMetadataQuery(campaignId);
	const campaign = usePortalCampaignQuery(campaignId);

	const campaignData = isNotFound(campaign) ? undefined : campaign.data;
	useEffect(() => {
		if (!campaignData) return;
		gtag('set', { campaign_id: campaignData.id });
		gtag('event', 'view_campaign', {
			campaign_id: campaignData.id,
			campaign_slug: campaignData.slug,
			campaign_name: campaignData.name,
		});
	}, [campaignData]);

	if (isNotFound(metadata) || isNotFound(campaign)) return null;

	const currentSlug = campaign.data.slugsByLocale[locale] ?? campaign.data.id;
	// Match the page's actual URL: at `/${slug}` (unprefixed) the canonical points to
	// `${baseUrl}/${slug}` so it matches x-default. At `/${locale}/${slug}` it points
	// to the prefixed URL. See note in `index.tsx`.
	const canonicalUrl = hasLocalePrefix() ? `${baseUrl}/${locale}/${currentSlug}` : `${baseUrl}/${currentSlug}`;
	const imageUrl = metadata.image?.location ? generateImageSrc(metadata.image.location) : undefined;
	const portalMetadata = usePortalMetadataQuery();
	const portalLocales = isNotFound(portalMetadata) ? [locale] : portalMetadata.locales;

	const firstCategory = campaign.data.categories[0];
	const categoryName =
		firstCategory && categoryMessages[firstCategory] ? t(categoryMessages[firstCategory]) : undefined;

	// WebPage JSON-LD
	const jsonLd: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		'name': metadata.title,
		...(metadata.description && { description: stripHtml(metadata.description) }),
		'url': canonicalUrl,
		...(imageUrl && { image: imageUrl }),
		...(campaign.data.sdgs.length > 0 && {
			about: campaign.data.sdgs.map((sdg) => ({
				'@type': 'Thing',
				'name': sdgMessages[sdg] ? t(sdgMessages[sdg]) : `SDG ${sdg}`,
				'url': sdgGoalUrl(sdg, locale),
			})),
		}),
		...(campaign.data.ngos.length > 0 && {
			sponsor: campaign.data.ngos.map((ngo) => ({
				'@type': 'NonprofitOrganization',
				'name': ngo.name,
			})),
		}),
		'potentialAction': {
			'@type': 'DonateAction',
			'name': t`Donate`,
			'target': `${canonicalUrl}/donate`,
			...(campaign.data.startDate && { startTime: campaign.data.startDate }),
			...(campaign.data.endDate && { endTime: campaign.data.endDate }),
		},
		// Voice search: tell assistants which parts of the page to read aloud
		'speakable': {
			'@type': 'SpeakableSpecification',
			'cssSelector': ['h1', '[data-speakable="description"]'],
		},
	};

	const portalTitle = isNotFound(portalMetadata) ? undefined : portalMetadata.texts.title;

	// BreadcrumbList: Home > Category > Campaign (or Home > Campaign if no category)
	const breadcrumbItems = [
		{ '@type': 'ListItem', 'position': 1, 'name': portalTitle ?? 'Home', 'item': `${baseUrl}/${locale}` },
	];
	if (categoryName && firstCategory) {
		breadcrumbItems.push({
			'@type': 'ListItem',
			'position': 2,
			'name': categoryName,
			'item': `${baseUrl}/${locale}?causes=${firstCategory}`,
		});
	}
	breadcrumbItems.push({
		'@type': 'ListItem',
		'position': categoryName ? 3 : 2,
		'name': metadata.title ?? campaign.data.name ?? '',
		'item': canonicalUrl,
	});
	const breadcrumbLd = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		'itemListElement': breadcrumbItems,
	};

	return (
		<Seo
			title={metadata.title}
			description={metadata.description}
			image={imageUrl}
			imageWidth={metadata.image?.width}
			imageHeight={metadata.image?.height}
			imageType={metadata.image?.type}
			imageAlt={campaign.data.heroImageAlt}
			canonicalUrl={canonicalUrl}
			locale={locale}
			alternateLocales={portalLocales}
			siteName={portalTitle}
			alternates={buildAlternatesWithSlugs(baseUrl, portalLocales, campaign.data.slugsByLocale, campaign.data.id)}
			jsonLd={[jsonLd, breadcrumbLd]}
		/>
	);
}
